import React from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Box, Typography, IconButton, Button } from "@mui/material";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ContactDrawer from "./ContactDrawer";

import { useTranslation } from "react-i18next"; // Mantener la importación para asegurar la carga de configuraciones de i18n
import { createCustomerPanelUi, CUSTOMER_PANEL_ACCENTS } from "./customerPanelStyles";

const Contacts = ({
  contacts,
  newContact,
  setNewContact,
  editContactId,
  setEditContactId,
  showContactForm,
  setShowContactForm,
  isDeletionAllowed,
  handleContactChange,
  handleContactSubmit,
  handleAddNewContact,
  handleEditContact,
  handleDeleteContact,
  canAdd,
  COLUMN_WIDTH,
}) => {
  const theme = useTheme();
  const accent = CUSTOMER_PANEL_ACCENTS.contacts;
  const ui = createCustomerPanelUi(theme, accent);
  const safeContacts = Array.isArray(contacts) ? contacts : [];

  const { t } = useTranslation();

  const styles = {
    fondoContacto: {
      ...ui.cardSx,
      width: "100%",
      marginBottom: 1,
      display: "flex",
      justifyContent: "space-between",
      gap: 0.75,
      padding: "0.75rem",
    },
  };
  const panelSx = {
    ...ui.panelSx,
    width: COLUMN_WIDTH,
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
  };
  const primaryButtonSx = ui.addButtonSx;
  const actionIconSx = {
    ...ui.actionIconSx,
  };

  const openAddDrawer = () => {
    setNewContact({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      position: "",
    });
    setEditContactId(null);
    setShowContactForm(true);
  };

  const openEditDrawer = (contact) => {
    setNewContact(contact);
    setEditContactId(contact.id);
    setShowContactForm(true);
  };

  return (
    <Box sx={panelSx}>
      {/* 🔹 Cabecera fija */}
      <Box sx={{ ...ui.headerSx, pl: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Typography sx={ui.headerEyebrowSx}>
            {t("Clientes.Contact")}
          </Typography>
          <Typography sx={ui.headerTitleSx}>
            {safeContacts.length}{" "}
            {safeContacts.length === 1 ? "contacto" : "contactos"}
          </Typography>
        </Box>

        <Button
          onClick={openAddDrawer}
          disabled={!canAdd}
          variant="outlined"
          sx={primaryButtonSx}
        >
          {t("Clientes.Add")}
        </Button>
      </Box>

      <Box sx={ui.bodySx}>
        {safeContacts.length === 0 ? (
          <Typography sx={ui.emptyStateSx}>
            {t("Contacts.NoContact")}
          </Typography>
        ) : (
          safeContacts.map((contact) => {
            const fullName =
              `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "-";
            const initials = fullName
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase())
              .join("");

            return (
              <Box key={contact.id} sx={styles.fondoContacto}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    minWidth: 0,
                    flex: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      ...ui.avatarSx,
                      fontSize: "0.82rem",
                    }}
                  >
                    {initials || "?"}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.45,
                        flexWrap: "wrap",
                        mb: 0.35,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.9rem",
                          color: "text.primary",
                          lineHeight: 1.1,
                        }}
                      >
                        {fullName}
                      </Typography>
                      {contact.position && (
                        <Box
                          sx={{
                            px: 0.55,
                            py: 0.18,
                          ...ui.pillSx,
                            fontSize: "0.64rem",
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                          }}
                        >
                          {contact.position}
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                      {contact.phone || "-"}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        color: "text.secondary",
                        overflowWrap: "anywhere",
                        fontSize: "0.78rem",
                      }}
                    >
                      {contact.email || "-"}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "flex-start",
                    gap: 0.45,
                    ml: 0.35,
                  }}
                >
                  <IconButton
                    onClick={() => openEditDrawer(contact)}
                    aria-label="edit"
                    sx={{
                      ...actionIconSx,
                      borderColor: alpha(accent, 0.24),
                    }}
                  >
                    <CreateOutlinedIcon sx={{ color: accent, fontSize: "0.92rem" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteContact(contact.id)}
                    aria-label="delete"
                    sx={{
                      ...actionIconSx,
                      borderColor: alpha(theme.palette.error.main, 0.24),
                    }}
                    disabled={!isDeletionAllowed}
                  >
                    <DeleteOutlineOutlinedIcon
                      sx={{ color: theme.palette.error.main, fontSize: "0.92rem" }}
                    />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <ContactDrawer
        open={showContactForm}
        onClose={() => setShowContactForm(false)}
        editContactId={editContactId}
        newContact={newContact}
        handleContactChange={handleContactChange}
        handleContactSubmit={handleContactSubmit}
      />
    </Box>
  );
};

export default Contacts;
